<template>
  <div ref="review" class="flex-grid" :class="[`product-review-section-${review.productReviewId}`]">
   <Divider class="review-section-divider"/>
    <hr/>
    <div class="flex-col-12-xs flex-col-12-sm flex-col-4-md flex-col-4-lg">
      <Rating :value="review.rating" :max="5"></Rating>
      <div class="product-review-details">
        <div class="product-review-name">{{ review.firstName }} {{ review.lastName }}</div>
        <span class="product-review-verified">
          <span class="product-review-verified-badge">
            <i class="af af-checkmark"></i><span class="product-review-verified-text">Verified Owner</span>
          </span>
        </span>
        <div v-if="review.featured && showFeatured">
          <div class="product-review-featured">Featured</div>
        </div>
      </div>
      <div v-if="review" class="product-review-details">{{ reviewDate }} - {{ reviewCity }}, {{ review.regionCode }}</div>
      <div>
        <a v-if="showProduct" :href="$router.resolve({ name: 'product', params: { id: review.productHashKey, slug: review.productSlug } }).href" class="product-review-product-link">
          <div class="product-review-product">
            <div class="product-review-product-image"><img :src="review.productImageUrl" /></div>
            <div class="product-review-product-title">{{ review.productTitle }}</div>
          </div>
        </a>
      </div>
    </div>
    <div class="flex-col-12-xs flex-col-12-sm flex-col-8-md flex-col-8-lg">
      <div class="product-review-ratings">
        <div v-if="review.value" class="product-review-rating">
          <div class="product-review-rating-title">Value:</div>
          <Dots :value="review.value" :max="5"></Dots>
        </div>
        <div v-if="review.design" class="product-review-rating">
          <div class="product-review-rating-title">Design:</div>
          <Dots :value="review.design" :max="5"></Dots>
        </div>
        <div v-if="review.quality" class="product-review-rating">
          <div class="product-review-rating-title">Quality:</div>
          <Dots :value="review.quality" :max="5"></Dots>
        </div>
      </div>

      <div v-if="comment" class="product-review-comment">
        <ReadMore :text="comment" :limit-by-char="showReadMore" :limit=160>
          <template v-slot:empty>
            This person let their star rating speak for itself.
          </template>
          <template v-slot:readMore>
            More
          </template>
        </ReadMore>
        <div v-if="review.images && review.images.length > 0" class="row review-images-container">
          <div v-for="(image, i) in review.images.slice(0, 5)" :key="image.thumbnailUrl" class="review-image">
            <img
                :data-src="image.thumbnailUrl"
                data-toggle="reviewModal"
                data-target="#myModal"
                :class="`productReviewImage lazyload image-lazy`"
                @click="setupModal(image, `${review.firstName} ${review.lastName}`, i)"
                :alt="`Thumbnail of ${review.productTitle} provided by ${review.firstName} - ${i+1} of ${currentImages.length}.`"
            />
          </div>
          <Modal ref="reviewModal" :show="showModal" :show-close="true" @update:show="closeModal">
            <div class="modal-container">
              <div class="modal-title">
                <p>{{ review.productTitle }}</p>
                <p class="secondary">by {{ modalUsername }}</p>
              </div>
              <div ref="swiper" class="swiper-container">
                <div class="swiper-wrapper">
                  <div v-for="(image, i) in currentImages" :key="i" class="swiper-slide">
                    <img 
                      :src="image.imageUrl" 
                      class="swiper-slide-image" 
                      :alt="`Large view of ${review.productTitle} provided by ${review.firstName} - ${i+1} of ${currentImages.length}.`" 
                    />
                  </div>
                </div>
                <template v-if="currentImages.length > 1">
                  <div class="swiper-pagination"></div>
                  <div class="swiper-button-prev"><span class="af af-chevron-left"></span></div>
                  <div class="swiper-button-next"><span class="af af-chevron-right"></span></div>
                </template>
              </div>
            </div>
          </Modal>
        </div>
      </div>

      <div v-if="review.response" class="product-review-response">
        <p class="response-title">Response From Article</p>
        <ReadMore :text="review.response" :limit-by-char="showReadMore" :limit=160>
          <template v-slot:readMore>
            More
          </template>
        </ReadMore>
      </div>
    </div>
  </div>

</template>
<script>
import { Modal,Divider } from '@/pixel/index';
import timeago from 'timeago.js';
import ImportUtils from '@/components/commons/ImportUtils.js';
import Tracking from '@/components/commons/Tracking.js';
import Rating from './Rating.vue';
import Dots from './Dots.vue';
import ReadMore from './ReadMore.vue';

import { trackReviewViewed } from './ProductReviewTracking';
import 'intersection-observer';

export default {
  name: 'ProductReviewSection',
  components: {
    Divider,
    Rating,
    Dots,
    ReadMore,
    Modal,
  },
  props: {
    review: {
      type: Object,
      default: null
    },
    showProduct: {
      type: Boolean,
      default: false
    },
    showFeatured: {
      type: Boolean,
      default: true
    },
    product: {
      default: null,
      type: Object,
    },
    showReadMore: {
      type: Boolean,
      default: false,
    }

  },
  data() {
    return {
      modalUsername: null,
      showModal: false,
      swiper: null,
      currentImages: [],
      index: 0,
      modalImageUrl: null,
    };
  },
  computed: {
    reviewDate() {
      return timeago().format(this.review.reviewDate);
    },
    reviewCity() {
      if (typeof this.review.city === 'string') {
        const lowerCaseCity = this.review.city.toLowerCase();
        const capitalisedCity = lowerCaseCity
            .split(' ')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
        return capitalisedCity;
      }
      return this.review.city;
    },
    comment() {
      if(this.review && this.review.comments && this.review.comments.length) {
        return this.review.comments[this.review.comments.length - 1];
      }
      return '';
    }
  },

  created() {
    this.setupReviewImages();
  },

  mounted() {
    try {
      this.observer = new IntersectionObserver(
          (entries) => {
            if (entries && entries.length && entries[0].isIntersecting) {
              trackReviewViewed(this.review);
              this.observer.unobserve(this.$refs.review);
              this.observer = null;
            }
          },
          {
            threshold: 1.0,
          },
      );
      this.observer.observe(this.$refs.review);
    } catch (e) {
      console.error(e);
    }
  },

  beforeDestroy() {
    if (this.observer) {
      this.observer.unobserve(this.$refs.review);
      this.observer = null;
    }
  },

  methods: {
    setupReviewImages() {
      if (!this.review.images || !this.review.images.length) return;
      this.currentImages = this.review.images;
    },

    async setupSwiper() {
      const Swiper = await ImportUtils.getSwiper();
      this.swiper = new Swiper(this.$refs.swiper, {
        slidesPerView: 1,
        spaceBetween: 0,
        initialSlide: this.index ?? 0,
        loop: this.currentImages.length > 1,
        pagination: {
          el: '.swiper-pagination',
          clickable: true,
          dynamicBullets: true,
          type: 'bullets',
        },
        navigation: {
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        },
        keyboard: true,
      });

      this.swiper.on('slideChange', () => {
        Tracking.track('ProductReviewImage', {
          index: this.swiper.realIndex,
          productReviewImage: this.modalImageUrl,
        });
      });
    },
    setupModal(image, username, index) {
      this.showModal = true;
      this.modalImageUrl = image.imageUrl;
      this.modalUsername = username;
      this.index = index;

      Tracking.track('ProductReviewImage', {
        index,
        productReviewImage: image.imageUrl,
      });

      this.setupSwiper();
    },
    closeModal() {
      this.showModal = false;
    },
  },
};
</script>

<style lang="postcss">
@import '@/pixel/style/variables';

  .product-review {
    padding-top: var(--space-6);
    padding-bottom: var(--space-6);
  }
 .review-section-divider {
  width:100%;
  height: 2px!important;
}

  .product-review-details {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
  }

  .product-review-name {
    font-size: var(--font-size-16);
    font-family: var(--font-bold);
    margin-right: var(--space-2);
  }

  .product-review-ratings {
    display: flex;

    @media (max-width: calc($mobile-breakpoint - 1px)) {
      margin-top: var(--space-4);
    }
  }

  .product-review-rating {
    margin-bottom: var(--space-4);

    &:not(:last-child) {
      margin-right: var(--space-10);

      @media (max-width: calc($small-breakpoint - 1px)) {
        margin-right: var(--space-4);
      }
    }
  }

  .product-review-rating-title {
    font-size: var(--font-size-14);
    line-height: var(--line-height-14);
    font-family: var(--font-bold);
    margin-bottom: var(--space-1);
  }

  .product-review-featured {
    background-color: var(--grey-ultra-light);
    padding: 2px var(--space-2);
    font-size: var(--font-size-12);
    line-height: normal;
    border-radius: 12px;
  }

  .product-review-verified-text {
    display: none;
    margin-left: var(--space-1);
  }

  .product-review-comment {
    max-width: 34em;
  }

  .product-review-verified {
    background-color: var(--white);
    padding: 2px var(--space-2);
    border-radius: 12px;
    font-size: var(--font-size-12);
    line-height: normal;
    margin-right: var(--space-1);

    &:hover {
      background-color: var(--grey-ultra-light);

      .product-review-verified-text {
        display: block;
      }
    }
  }

  .product-review-verified-badge {
    display: flex;
  }

  .product-review-product-link {
    display: inline-block;
  }

  .product-review-product {
    display: flex;
    align-items: center;
    margin-top: var(--space-4);
  }

  .product-review-product-image {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border: 1px solid var(--grey-ultra-light);
  }

  .product-review-product-title {
    font-size: var(--font-size-14);
    line-height: var(--line-height-14);
    color: var(--grey-medium);
    margin-left: var(--space-4);
  }

  .product-review-response {
    background-color: var(--grey-ultra-light);
    margin-top: var(--space-4);
    padding: var(--space-4);
    box-sizing: border-box;
    border-top-right-radius: var(--button-border-radius);
    max-width: 34em;
  }

  .response-title {
    margin: 0 0 var(--space-2) 0;
  }

  @media (max-width: calc($mobile-breakpoint - 1px)) {
    .review-images-container {
      margin-top: 16px;
    }
  }

  .review-image {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 1px;
    cursor: pointer;
    background-color: var(--background-color);
  }

  .review-images-container {
    display: inline-flex;
    margin-top: 16px;

    .modal-container {
      padding: 0;
      max-width: 800px;
      width: 100%;

      .modal-title {
        min-height: var(--space-12);
        font-size: var(--font-size);
        font-weight: 600;
        color: var(--grey-dark);
        padding: var(--space-2) var(--space-16) var(--space-2) var(--space-4);

        p {
          margin: 0px;
          padding: 0px;

          &.secondary {
            font-size: 12px;
            font-weight: 400;
          }
        }
      }
    }

    @media (max-width: calc($mobile-breakpoint - 1px)) {
      .modal-container {
        width: 100%;
        height: 100vh;
        background: var(--grey-ultra-light);

        .modal-title {
          overflow-y: hidden;
          background: $white;
          margin-left: 0;
          width: 100%;

          p {
            margin-left: 15px;
          }
        }

        .swiper-container {
          height: 50vh;
          top: 15vh;
          min-width: unset;

          .swiper-pagination {
            position: fixed;
          }
        }
      }
    }

    .swiper-container {
      width: 100%;
      min-width: 700px;
      margin-left: auto;
      margin-right: auto;

      div {
        .swiper-slide {
          width: 100%;
          text-align: center;
          font-size: 18px;

          /* Center slide text vertically */
          display: -webkit-box;
          display: -ms-flexbox;
          display: -webkit-flex;
          -webkit-box-pack: center;
          -ms-flex-pack: center;
          -webkit-justify-content: center;
          justify-content: center;
          -webkit-box-align: center;
          -ms-flex-align: center;
          -webkit-align-items: center;
          align-items: center;
          background-color: var(--grey-ultra-light);

          img.swiper-slide-image {
            width: auto;
            height: auto;
            max-height: 100%;
          }
        }
      }

      .swiper-pagination-bullet-active-main {
        background: var(--coral);
      }

      .swiper-button-next,
      .swiper-button-prev {
        background-image: none;
        display: flex;
        font-size: 1em;
        width: 32px;
        height: 32px;
        top: 46%;
        padding: 8px;
        color: var(--link-color);
        margin-top: inherit;
        background-color: $white;
        justify-content: center;
        box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.2);
        transition: background-color 0.3s;
        border-radius: 50%;
        cursor: pointer;

        @media (max-width: calc($tablet-breakpoint - 1px)) {
          display: none;
        }

        &.swiper-button-disabled {
          opacity: 0;
        }

        .af {
          align-self: center;
          padding: 10px;
        }
      }

      .swiper-button-next {
        right: 16px;
      }

      .swiper-button-prev {
        left: 16px;
      }
    }

    .modal {
      .body {
        padding: unset;
      }

    }
  }


</style>
